import createIntroHintNode from './createIntroHintNode';
import sendInitialHintSeenSignal from './sendInitialHintSeenSignal';
import shouldShowInitialHint, { InitialHintState } from './shouldShowInitialHint';
import type { SonoraViewState } from 'owa-editor-sonora-types';
import { trace } from 'owa-trace';
import { ContentPosition, IEditor } from 'roosterjs-editor-types';

// Shows the initial hint to the user by appending it in a span of grey text right
// at the begin of the container of the compose editor.
export default function handleInitialHintInEditor(
    viewState: SonoraViewState,
    editor: IEditor | undefined,
    nodeText: string,
    triggerCharacter: string
): HTMLElement | null {
    if (editor) {
        // Check if Personalization service says we should show the initial hint.
        const initialHintState: InitialHintState = shouldShowInitialHint();

        // Insert hint to editor if all of the following conditions are satisfied:
        // 1. nodeText is valid.
        // 2. Personalization service tells us to show the hint.
        // 3. Sonora callout is not opened.
        // 4. The first line of the compose editor is empty.
        if (
            nodeText &&
            initialHintState.showInitialHint &&
            !viewState.isSuggesting &&
            isFirstLineEmpty(editor)
        ) {
            const node = createIntroHintNode(editor, nodeText, triggerCharacter);
            trace.info('[Sonora Plugin] Adding initial hint to editor.');
            editor.insertNode(node, {
                position: ContentPosition.Begin,
                updateCursor: false,
                replaceSelection: true,
                insertOnNewLine: false,
            });

            // Inform Personalization service that user is seeing hint text.
            // The sendSignalToPersonalizationService field tells us that the Personalization
            // service explicitly told us to show the ghost text, as opposed to us showing the ghost
            // text only because the Personalization suggestions had not loaded yet.
            if (initialHintState.sendSignalToPersonalizationService) {
                sendInitialHintSeenSignal();
            }

            return node;
        }
    }

    return null;
}

function isFirstLineEmpty(editor: IEditor): boolean {
    const firstLine = editor.getBodyTraverser().currentInlineElement.getTextContent();
    return !firstLine;
}
